<template>
  <div class="navbar-address-selector">
    <v-menu offset-y :close-on-content-click="false" :nudge-bottom="4">
      <template v-slot:activator="{ props }">
        <v-btn
          v-bind="props"
          variant="text"
          class="address-btn text-none"
          :loading="pending"
        >
          <template #prepend>
            <v-icon color="primary" size="18">mdi-map-marker</v-icon>
          </template>
          
          <div class="address-content">
            <div class="address-label">Service Location</div>
            <div 
              class="address-text"
              v-tooltip="isAddressTextTruncated ? fullAddressText : null"
            >
              {{ currentAddressText }}
            </div>
          </div>
          
          <template #append>
            <v-icon color="medium-emphasis" size="20">mdi-chevron-down</v-icon>
          </template>
        </v-btn>
      </template>

      <v-list class="address-dropdown-menu">
        <!-- Scrollable Address List -->
        <div class="scrollable-addresses">
          <v-list-item
            v-for="address in addresses"
            :key="address.id"
            @click="selectAddress(address)"
            :class="{ 'address-item--selected': address.is_default }"
            class="address-list-item"
          >
            <template #prepend>
              <div class="address-icon-container">
                <v-icon 
                  :color="getAddressIcon(address).color"
                  size="20"
                >
                  {{ getAddressIcon(address).icon }}
                </v-icon>
              </div>
            </template>

            <div class="address-item-content">
              <v-list-item-title class="d-flex align-center">
                {{ address.address_nickname || address.address_line_1 || 'Address' }}
                <v-chip
                  v-if="address.is_default"
                  size="x-small"
                  color="primary"
                  class="ml-2"
                >
                  Current
                </v-chip>
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ formatAddress(address) }}
              </v-list-item-subtitle>
            </div>
          </v-list-item>
        </div>

        <!-- Fixed Action Items at Bottom -->
        <div class="fixed-actions">
          <v-divider class="action-divider" />
          
          <v-list-item @click="handleAddNewAddress" class="action-item add-address-item">
            <template #prepend>
              <div class="address-icon-container">
                <v-icon color="primary" size="20">mdi-plus</v-icon>
              </div>
            </template>
            <div class="address-item-content">
              <v-list-item-title class="action-title">
                Add new address
              </v-list-item-title>
            </div>
          </v-list-item>

          <v-list-item @click="navigateToManageAddresses" class="action-item">
            <template #prepend>
              <div class="address-icon-container">
                <v-icon color="grey-darken-1" size="20">mdi-cog</v-icon>
              </div>
            </template>
            <div class="address-item-content">
              <v-list-item-title class="action-title">
                Manage addresses
              </v-list-item-title>
            </div>
          </v-list-item>
        </div>
      </v-list>
    </v-menu>

    <!-- Add/Edit Address Form Dialog -->
    <v-dialog 
      v-model="showAddressForm" 
      max-width="500px" 
      persistent
      scrollable
    >
      <SimpleAddressForm
        :edit-address="editingAddress"
        @close="closeAddressForm"
        @success="handleAddressSuccess"
      />
    </v-dialog>
  </div>
</template>

<script>
import addressService from '@/services/AddressService';
import useSnackbar from '@/composables/useSnackbar';
import SimpleAddressForm from './SimpleAddressForm.vue';

export default {
  name: 'NavbarAddressSelector',
  components: {
    SimpleAddressForm
  },
  emits: ['address-selected'],
  data() {
    return {
      pending: false,
      addresses: [],
      currentAddress: null,
      showAddressForm: false,
      editingAddress: null
    };
  },
  computed: {
    currentAddressText() {
      if (!this.currentAddress) {
        return 'Select location';
      }
      
      // Format address for navbar display (shorter version)
      const parts = [];
      if (this.currentAddress.address_line_1) {
        parts.push(this.currentAddress.address_line_1);
      }
      if (this.currentAddress.suburb) {
        parts.push(this.currentAddress.suburb);
      }
      if (this.currentAddress.place_city) {
        parts.push(this.currentAddress.place_city);
      }
      
      const addressText = parts.filter(Boolean).join(', ');
      
      // Truncate if too long for navbar
      if (addressText.length > 15) {
        return addressText.substring(0, 15) + '...';
      }
      
      return addressText || 'Address';
    },
    
    fullAddressText() {
      if (!this.currentAddress) {
        return 'Select location';
      }
      
      // Format full address for tooltip
      const parts = [];
      if (this.currentAddress.address_line_1) {
        parts.push(this.currentAddress.address_line_1);
      }
      if (this.currentAddress.suburb) {
        parts.push(this.currentAddress.suburb);
      }
      if (this.currentAddress.place_city) {
        parts.push(this.currentAddress.place_city);
      }
      
      return parts.filter(Boolean).join(', ') || 'Address';
    },
    
    isAddressTextTruncated() {
      return this.fullAddressText.length > 15;
    }
  },
  async mounted() {
    await this.loadAddresses();
  },
  methods: {
    async loadAddresses() {
      this.pending = true;
      const { showSnackbar } = useSnackbar();
      
      try {
        const { data } = await addressService.getAddresses();
        this.addresses = data.value || [];
        
        // Find the default address
        this.currentAddress = this.addresses.find(addr => addr.is_default) || this.addresses[0] || null;
        
        console.log('Current address loaded:', this.currentAddress);
      } catch (err) {
        console.error('Failed to load current address:', err);
        this.addresses = [];
        this.currentAddress = null;
        
        // Only show error if it's not a 404 (no addresses)
        if (err.response?.status !== 404) {
          showSnackbar({
            text: 'Failed to load address information.',
            state: 'error'
          });
        }
      } finally {
        this.pending = false;
      }
    },
    formatAddress(address) {
      if (address.full_address) {
        return address.full_address;
      }
      
      const parts = [];
      if (address.address_line_1) parts.push(address.address_line_1);
      if (address.suburb) parts.push(address.suburb);
      if (address.place_city) parts.push(address.place_city);
      if (address.place_state_short_name) parts.push(address.place_state_short_name);
      
      return parts.filter(Boolean).join(', ') || 'Address not available';
    },
    getAddressIcon(address) {
      const nickname = (address.address_nickname || '').toLowerCase();
      
      if (nickname.includes('home')) {
        return { icon: 'mdi-home', color: 'primary' };
      } else if (nickname.includes('work') || nickname.includes('office')) {
        return { icon: 'mdi-briefcase', color: 'orange' };
      } else if (nickname.includes('school') || nickname.includes('university')) {
        return { icon: 'mdi-school', color: 'blue' };
      } else {
        return { icon: 'mdi-map-marker', color: 'grey' };
      }
    },
    async selectAddress(address) {
      const { showSnackbar } = useSnackbar();
      
      try {
        console.log('Setting address as default:', address.id);
        await addressService.setDefaultAddress(address.id);
        
        // Update local state
        this.addresses.forEach(addr => addr.is_default = false);
        address.is_default = true;
        this.currentAddress = address;
        
        showSnackbar({
          text: `Location set to ${this.formatAddress(address)}`,
          state: 'success'
        });
        
        this.$emit('address-selected', address);
      } catch (err) {
        console.error('Failed to set default address:', err);
        showSnackbar({
          text: 'Failed to update location. Please try again.',
          state: 'error'
        });
      }
    },
    handleAddNewAddress() {
      this.editingAddress = null;
      this.showAddressForm = true;
    },
    closeAddressForm() {
      this.showAddressForm = false;
      this.editingAddress = null;
    },
    async handleAddressSuccess(newAddress) {
      // Refresh the address list
      await this.loadAddresses();
      
      this.closeAddressForm();
      
      const { showSnackbar } = useSnackbar();
      showSnackbar({
        text: `Address ${this.editingAddress ? 'updated' : 'added'} successfully`,
        state: 'success'
      });
      
      // Emit address-selected event if this is a new address and becomes default
      if (newAddress && (newAddress.is_default || this.addresses.length === 1)) {
        this.$emit('address-selected', newAddress);
        // Also emit window event for global listening
        window.dispatchEvent(new CustomEvent('address-changed', { detail: newAddress }));
      }
    },
    navigateToManageAddresses() {
      // Navigate to the addresses management page
      this.$router.push('/addresses');
    }
  }
};
</script>

<style lang="scss" scoped>
.navbar-address-selector {
  width: 100%;
  
  .address-btn {
    height: 44px !important;
    padding: 0 !important;
    border-radius: 8px !important;
    max-width: 100% !important;
    width: 100% !important;
    background-color: rgba(var(--v-theme-primary), 0.1) !important;
    border: 1px solid rgba(var(--v-theme-outline), 0.2);
    position: relative;
    
    &:hover {
      background-color: rgba(var(--v-theme-primary), 0.15) !important;
      border-color: rgba(var(--v-theme-primary), 0.3);
    }
    
    :deep(.v-btn__content) {
      display: flex;
      align-items: center;
      width: 100%;
      justify-content: flex-start;
      padding: 0 12px 0 0;
    }
    
    :deep(.v-btn__prepend) {
      position: absolute !important;
      left: 17px !important;
      top: 50% !important;
      transform: translateY(-50%) !important;
      margin: 0 !important;
      width: 24px !important;
      height: 24px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
    }
    
    :deep(.v-btn__append) {
      position: absolute !important;
      right: 12px !important;
      top: 50% !important;
      transform: translateY(-50%) !important;
      margin: 0 !important;
      opacity: 0.8;
      transition: all 0.2s ease;
      
      .v-icon {
        font-size: 20px !important;
        color: rgba(var(--v-theme-on-surface), 0.7) !important;
      }
    }
  }
  
  .address-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
    flex: 1;
    gap: 3px;
    margin-left: 53px;
    padding-right: 40px;
  }
  
  .address-label {
    font-size: 11px;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface), 0.8);
    line-height: 1.2;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .address-text {
    font-size: 14px;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface)) !important;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 140px;
  }
}

.address-dropdown-menu {
  min-width: 300px !important;
  max-width: 350px !important;
  max-height: 400px !important;
  border-radius: 16px !important;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
  border: 1px solid rgba(var(--v-theme-outline), 0.06) !important;
  background: rgba(var(--v-theme-surface), 1) !important;
  margin-top: 8px !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  backdrop-filter: blur(20px);
  
  :deep(.v-list) {
    padding: 0 !important;
    background: transparent;
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  
  .scrollable-addresses {
    flex: 1;
    overflow-y: auto !important;
    overflow-x: hidden;
    padding: 12px !important;
    max-height: 280px;
    
    &::-webkit-scrollbar {
      width: 4px;
    }
    
    &::-webkit-scrollbar-track {
      background: rgba(var(--v-theme-surface-variant), 0.1);
      border-radius: 2px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: rgba(var(--v-theme-primary), 0.2);
      border-radius: 2px;
      
      &:hover {
        background: rgba(var(--v-theme-primary), 0.4);
      }
    }
  }
  
  .fixed-actions {
    flex-shrink: 0;
    background: rgba(var(--v-theme-surface), 0.98);
    border-top: 1px solid rgba(var(--v-theme-outline), 0.04);
    padding: 8px 12px 12px 12px !important;
    backdrop-filter: blur(10px);
    
    .action-divider {
      margin: 0 0 8px 0 !important;
      opacity: 0.1;
    }
  }
  
  .address-list-item,
  .action-item {
    border-radius: 12px !important;
    margin: 2px 0 !important;
    padding: 0 !important;
    min-height: 54px !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    display: flex !important;
    align-items: center !important;
    position: relative;
    flex-shrink: 0;
    
    &:hover {
      background-color: rgba(var(--v-theme-primary), 0.08) !important;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(var(--v-theme-primary), 0.15) !important;
    }
    
    :deep(.v-list-item__prepend) {
      position: absolute !important;
      left: 16px !important;
      top: 50% !important;
      transform: translateY(-50%) !important;
      margin: 0 !important;
      width: 24px !important;
      height: 24px !important;
      
      .address-icon-container {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border-radius: 6px;
        background: rgba(var(--v-theme-primary), 0.1);
        transition: all 0.2s ease;
        
        .v-icon {
          font-size: 18px !important;
        }
      }
    }
    
    .address-item-content {
      margin-left: 56px !important;
      padding: 16px 20px 16px 0 !important;
      width: calc(100% - 56px) !important;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }
    
    .v-list-item-title {
      font-weight: 600 !important;
      font-size: 15px !important;
      color: rgba(var(--v-theme-on-surface), 0.95) !important;
      line-height: 1.4;
      margin-bottom: 4px !important;
      
      &.action-title {
        color: rgba(var(--v-theme-on-surface), 0.9) !important;
        font-weight: 500 !important;
      }
      
      .v-chip {
        font-size: 10px !important;
        font-weight: 600 !important;
        height: 20px !important;
        border-radius: 10px !important;
        margin-left: 8px !important;
      }
    }
    
    .v-list-item-subtitle {
      font-size: 13px !important;
      color: rgba(var(--v-theme-on-surface), 0.6) !important;
      line-height: 1.4;
      font-weight: 400 !important;
    }

    &:hover {
      :deep(.v-list-item__prepend) {
        .address-icon-container {
          background: rgba(var(--v-theme-primary), 0.15);
          transform: scale(1.05);
        }
      }
    }
  }
}

.address-item--selected {
  background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.06) 0%, rgba(var(--v-theme-primary), 0.04) 100%) !important;
  border: 1px solid rgba(var(--v-theme-primary), 0.12) !important;
  box-shadow: 0 1px 4px rgba(var(--v-theme-primary), 0.08) !important;
  
  .v-list-item-title {
    color: rgba(var(--v-theme-on-surface), 0.95) !important;
    font-weight: 600 !important;
  }
  
  .v-list-item-subtitle {
    color: rgba(var(--v-theme-on-surface), 0.7) !important;
  }
  
  :deep(.v-list-item__prepend) {
    .address-icon-container {
      background: rgba(var(--v-theme-primary), 0.12) !important;
      
      .v-icon {
        color: rgb(var(--v-theme-primary)) !important;
      }
    }
  }
  
  &:hover {
    background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.08) 0%, rgba(var(--v-theme-primary), 0.06) 100%) !important;
    border-color: rgba(var(--v-theme-primary), 0.15) !important;
    
    :deep(.v-list-item__prepend) {
      .address-icon-container {
        background: rgba(var(--v-theme-primary), 0.15) !important;
      }
    }
  }
}

// Mobile responsiveness
@media (max-width: 600px) {
  .navbar-address-selector {
    .address-btn {
      height: 40px !important;
      padding: 0 10px !important;
    }
    
    .address-text {
      max-width: 120px;
      font-size: 12px;
    }
    
    .address-label {
      font-size: 9px;
    }
  }
  
  .address-dropdown-menu {
    min-width: 280px !important;
    max-width: 320px !important;
    border-radius: 12px !important;
    margin-top: 6px !important;
    
    .scrollable-addresses {
      padding: 8px !important;
    }
    
    .address-list-item,
    .action-item {
      min-height: 48px !important;
      border-radius: 10px !important;
      
      .address-item-content {
        padding: 12px 16px 12px 0 !important;
      }
      
      .v-list-item-title {
        font-size: 14px !important;
      }
      
      .v-list-item-subtitle {
        font-size: 12px !important;
      }
    }
  }
}

// Dark mode support
.v-theme--dark {
  .navbar-address-selector {
    .address-btn {
      background-color: rgba(var(--v-theme-surface-variant), 0.4) !important;
      border-color: rgba(var(--v-theme-outline), 0.3);
      
      &:hover {
        background-color: rgba(var(--v-theme-primary), 0.08) !important;
        border-color: rgba(var(--v-theme-primary), 0.4);
      }
    }
  }
  
  .address-dropdown-menu {
    background: rgba(var(--v-theme-surface), 0.95) !important;
    border-color: rgba(var(--v-theme-outline), 0.15) !important;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4) !important;
    backdrop-filter: blur(30px);
    
    .fixed-actions {
      background: rgba(var(--v-theme-surface), 0.9) !important;
      border-top-color: rgba(var(--v-theme-outline), 0.08) !important;
    }
    
    .address-list-item,
    .action-item {
      &:hover {
        background-color: rgba(var(--v-theme-primary), 0.12) !important;
        box-shadow: 0 4px 12px rgba(var(--v-theme-primary), 0.2) !important;
      }
      
      :deep(.v-list-item__prepend) {
        .address-icon-container {
          background: rgba(var(--v-theme-primary), 0.15) !important;
        }
      }
    }
    
    .address-item--selected {
      background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.18) 0%, rgba(var(--v-theme-primary), 0.12) 100%) !important;
      border-color: rgba(var(--v-theme-primary), 0.3) !important;
      box-shadow: 0 2px 8px rgba(var(--v-theme-primary), 0.2) !important;
      
      :deep(.v-list-item__prepend) {
        .address-icon-container {
          background: rgba(var(--v-theme-primary), 0.3) !important;
        }
      }
    }
  }
}

// Enhanced tooltip styling
:deep(.v-tooltip__content) {
  background-color: rgba(var(--v-theme-surface-variant), 0.95) !important;
  color: rgba(var(--v-theme-on-surface), 0.9) !important;
  border-radius: 6px !important;
  font-size: 11px !important;
  font-weight: 500 !important;
  padding: 6px 10px !important;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(var(--v-theme-outline), 0.2);
}

// Hover effect for chevron
.address-btn:hover {
  :deep(.v-btn__append) {
    opacity: 1;
    
    .v-icon {
      color: rgba(var(--v-theme-primary), 0.8) !important;
    }
  }
}
</style> 